import { USER_TAG_SUFFIX } from '../../../config';
import { getCurrencyOptions, getCurrencySymbol, isGOGCurrencyCode } from '../../lib/Amalgam';
import { getCachedCatalogueFilters } from '../../lib/Catalogue';
import { getDisplayTag, getSelectedTagsForApi, getTags, getVisibleTags } from '../../lib/Tags';
import { isDesktopView } from '../../lib/Utils';

/**
 * All the below defined keys are used for rendering and collecting filters in catalogue
 */
// key against display label for the filter in catalogue filters
const fieldLabel = 'fieldLabel';
// key against selected filters value will be saved in form of array
const fieldKey = 'fieldKey';
// selection type for the filter one of - `FILTER_TYPES`
const fieldType = 'fieldType';

// keys for saving the selection
export const tagsFilterKey = 'tags';
export const customFieldsFilterKey = 'customFields';
export const priceFilterKeys = {
  label: 'Price',
  valueKey: 'price'
};

// Filter field types - based in selection value
export const FILTER_TYPES = {
  SINGULAR: 'SINGULAR',
  MULTIPLE: 'MULTIPLE',
  NUMERIC: 'NUMERIC'
};
export const CUSTOM_FIELD_TYPES = {
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  BOOLEAN: 'BOOLEAN'
};
export const NUMERIC_TYPE_FILTER_CONFIG = {
  ltKey: 'lte',
  gtKey: 'gte',
  eqKey: 'eq'
};

const isPriceFilterEnabled = () => true;

/**
 * Get label for filter field and filter option in case of selection type field
 * @param option filter field
 * @returns displable label of filter field
 */
export const getFilterLabel = option => {
  if (option && option[fieldLabel]) {
    return option[fieldLabel];
  }
  return '';
};

/**
 * Get api key for filter field
 * @param option filter field
 * @returns key against with selection will be saved and sent into the api
 */
export const getFilterKey = option => {
  if (!option) {
    return;
  }
  return option[fieldKey] || option[fieldLabel];
};

/**
 * @returns filter type for the filter field - SELECTION_TYPE
 */
export const getFilterType = filter => filter[fieldType];

/**
 * @returns available filter options for the filter
 */
export const getFiltersOptions = filter => (filter || {}).options || [];

export const getFilterConfig = filter => (filter || {}).config || {};

export const isNumericTypeFilter = filter => {
  const filterType = getFilterType(filter);
  return filterType === FILTER_TYPES.NUMERIC;
};

export const formatNumericFilterValue = (selectedFilterValues, newValues) => {
  const [previousNumericValue = {}] = selectedFilterValues || [];
  const [min, max] = newValues;

  if (!min || !max) {
    return [];
  }

  if (
    min !== previousNumericValue[NUMERIC_TYPE_FILTER_CONFIG.gtKey] ||
    max !== previousNumericValue[NUMERIC_TYPE_FILTER_CONFIG.ltKey]
  ) {
    return [{ [NUMERIC_TYPE_FILTER_CONFIG.gtKey]: min, [NUMERIC_TYPE_FILTER_CONFIG.ltKey]: max }];
  }
};

export const getNumericFiltersValue = (selectedFilters, filter) => {
  const filterKey = getFilterKey(filter);
  const selectedValues = selectedFilters[filterKey];
  if (!selectedValues || !selectedValues[0]) {
    return [];
  }

  return [
    selectedValues[0][NUMERIC_TYPE_FILTER_CONFIG.gtKey] || '',
    selectedValues[0][NUMERIC_TYPE_FILTER_CONFIG.ltKey] || ''
  ];
};

export const shouldRenderFilter = filter => {
  if (isNumericTypeFilter(filter)) {
    return true;
  }
  const options = getFiltersOptions(filter);
  return Array.isArray(options) && options.length;
};

const getCustomFieldOptionsAndConfig = customFieldConfig => {
  const { fieldName, fieldType: customFieldType, stringPotentialValues } = customFieldConfig || {};
  switch (customFieldType) {
    case CUSTOM_FIELD_TYPES.STRING: {
      const options = (stringPotentialValues || []).map(value => ({
        [fieldLabel]: value,
        [fieldKey]: value
      }));
      return {
        options,
        [fieldType]: FILTER_TYPES.MULTIPLE, // for all string type custom fields show multiselect option
        config: customFieldConfig
      };
    }
    case CUSTOM_FIELD_TYPES.NUMBER: {
      return {
        options: [],
        [fieldType]: FILTER_TYPES.NUMERIC,
        config: customFieldConfig
      };
    }
    case CUSTOM_FIELD_TYPES.BOOLEAN: {
      return {
        // for all boolean type custom fields show multiselect option with one value
        options: [{ [fieldLabel]: fieldName, [fieldKey]: true }],
        [fieldType]: FILTER_TYPES.MULTIPLE,
        config: customFieldConfig
      };
    }
    default:
      return { options: [], config: customFieldConfig };
  }
};

const formatCustomFieldValue = ({ fieldId, customFieldType }, values) => {
  switch (customFieldType) {
    case CUSTOM_FIELD_TYPES.STRING:
      return { fieldId, stringValues: values };
    case CUSTOM_FIELD_TYPES.BOOLEAN:
      return { fieldId, booleanValue: values[0] };
    case CUSTOM_FIELD_TYPES.NUMBER:
      return { fieldId, numericValue: values[0] };
    default:
      return null;
  }
};

/**
 * Formats the filters from api
 * Also adds list of tags from catalogue amalgam
 * @param filters catalogue filters object from api
 * @returns list of filters to be render in catalogue filters modal
 */
export const formatFiltersForCatalogueView = filters => {
  // structure - { [fieldLabel]: 'abc', [fieldKey]: 'brands', [fieldType]: 'SINGULAR' }
  const catalogueFiltersList = [];

  if (!filters) {
    return catalogueFiltersList;
  }

  // formatting catalogue tags
  const isDesktop = isDesktopView();
  const tags = isDesktop ? getTags() : getVisibleTags();
  if (Array.isArray(tags) && tags.length) {
    const getLabel = tag => (isDesktop ? getDisplayTag(tag) : tag);
    const getKey = tag => (isDesktop ? tag : `${tag}${USER_TAG_SUFFIX}`);
    catalogueFiltersList.push({
      [fieldLabel]: 'Sub categories',
      [fieldKey]: tagsFilterKey,
      [fieldType]: FILTER_TYPES.MULTIPLE,
      options: tags.map(tag => ({
        [fieldLabel]: getLabel(tag),
        [fieldKey]: getKey(tag)
      }))
    });
  }

  const { variants } = filters;
  // formatting variants
  if (Array.isArray(variants)) {
    variants.forEach(variant => {
      catalogueFiltersList.push({
        [fieldLabel]: variant.variantTypeDisplay,
        [fieldKey]: variant.variantType,
        [fieldType]: FILTER_TYPES.MULTIPLE,
        options: variant.variantValues.map(variantValue => ({
          [fieldLabel]: variantValue.optionValue,
          [fieldKey]: variantValue.optionValue,
          rgbValue: variantValue.rgbValue
        }))
      });
    });
  }

  // adding price filter option
  if (isPriceFilterEnabled()) {
    catalogueFiltersList.push({
      [fieldLabel]: isGOGCurrencyCode() ? 'Grams' : priceFilterKeys.label,
      [fieldKey]: priceFilterKeys.valueKey,
      [fieldType]: FILTER_TYPES.NUMERIC,
      options: [],
      config: {
        inputAdornment: {
          component: getCurrencySymbol(),
          showOnRight: !!(getCurrencyOptions() || {}).symbolOnRight
        }
      }
    });
  }

  const { customFields } = filters;
  // formatting custom fields
  if (Array.isArray(customFields)) {
    customFields.forEach(customField => {
      const { fieldName, fieldId } = customField;
      catalogueFiltersList.push({
        [fieldLabel]: fieldName,
        [fieldKey]: fieldId,
        ...getCustomFieldOptionsAndConfig(customField)
      });
    });
  }

  // added other formated filters here if any or in the required order
  return catalogueFiltersList;
};

/**
 * Formats the selected filters object to filters object for api
 * @param {Object} filters selected filters object
 * @returns formatted filters object excepted in api
 */
export const formatFiltersToGetPaginatedAmalgam = filters => {
  if (!filters) {
    return null;
  }

  const apiFilters = {};
  const cachedCatalogueFilters = getCachedCatalogueFilters();
  const { variants } = cachedCatalogueFilters;
  // formatting variants
  if (Array.isArray(variants)) {
    variants.forEach(({ variantType }) => {
      const values = filters[variantType];
      if (Array.isArray(values) && values.length) {
        if (!apiFilters.variants) {
          apiFilters.variants = [];
        }
        apiFilters.variants.push({
          type: variantType,
          values
        });
      }
    });
  }

  if (isPriceFilterEnabled()) {
    const priceFilter = filters[priceFilterKeys.valueKey];

    if (Array.isArray(priceFilter) && priceFilter.length) {
      apiFilters[priceFilterKeys.valueKey] = priceFilter[0];
    }
  }

  const { customFields } = cachedCatalogueFilters;
  if (Array.isArray(customFields) && customFields.length) {
    customFields.forEach(({ fieldId, fieldType: customFieldType }) => {
      const values = filters[fieldId];
      if (Array.isArray(values) && values.length) {
        const formatedValue = formatCustomFieldValue({ fieldId, customFieldType }, values);
        if (formatedValue) {
          if (!apiFilters.customFields) {
            apiFilters.customFields = [];
          }
          apiFilters.customFields.push(formatedValue);
        }
      }
    });
  }

  // formatting tags
  const userDefinedSelectedTags = getSelectedTagsForApi();
  if (filters[tagsFilterKey] && userDefinedSelectedTags) {
    apiFilters[tagsFilterKey] = userDefinedSelectedTags;
  }

  return Object.keys(apiFilters).length ? apiFilters : null;
};
